import { useState, useMemo, useEffect } from "react";
import AdminTableSkeleton from "./AdminTableSkeleton";

function CommonTable({
  columns,
  data,
  loading,
  emptyText = "No data found",
  selectable = false,
  skeletonRows = 10,
  // Optional: show search bar (searches across specified keys)
  showSearch = false,
  searchPlaceholder = "Search all...",
  searchKeys = [],
  // Optional: show city dropdown filter
  showCityFilter = false,
  cityKey = "city",
  // Optional: show date filter (Any/Today)
  showDateFilter = false,
  dateKey = "created_at",
  dateLabel = "Date",
  // Optional: show date range filter (from/to)
  showDateRangeFilter = false,
  fromDateLabel = "From date",
  toDateLabel = "To date",
  // Optional: pagination (filter then paginate)
  pageSize,
  showPageSizeSelector = false,
  pageSizeOptions = [10, 25, 50, 100],
  // Optional: export to Excel (CSV download)
  showExport = false,
  exportFileName = "export",
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize || 10);

  const cities = useMemo(() => {
    if (!showCityFilter || !Array.isArray(data)) return [];
    const set = new Set();
    data.forEach((row) => {
      const c = row[cityKey];
      if (c != null && String(c).trim()) set.add(String(c).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data, showCityFilter, cityKey]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let out = data;
    const q = (searchQuery || "").trim().toLowerCase();
    if (q && (showSearch || searchKeys.length > 0)) {
      const keys = searchKeys.length > 0 ? searchKeys : null;
      out = out.filter((row) => {
        const values = keys
          ? keys.map((k) => row[k])
          : Object.values(row).filter((v) => v != null);
        const str = values
          .map((x) => (Array.isArray(x) ? x.join(" ") : String(x)))
          .join(" ")
          .toLowerCase();
        return str.includes(q);
      });
    }
    if (showCityFilter && cityFilter && cityFilter !== "all") {
      out = out.filter((row) => (row[cityKey] || "").trim() === cityFilter);
    }
    if (showDateFilter && dateFilter) {
      out = out.filter((row) => {
        const value = row?.[dateKey];
        if (!value) return false;
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return false;
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}` === dateFilter;
      });
    }
    if (showDateRangeFilter && (fromDateFilter || toDateFilter)) {
      out = out.filter((row) => {
        const value = row?.[dateKey];
        if (!value) return false;
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return false;

        if (fromDateFilter) {
          const fromDt = new Date(`${fromDateFilter}T00:00:00`);
          if (Number.isNaN(fromDt.getTime()) || dt < fromDt) return false;
        }
        if (toDateFilter) {
          const toDt = new Date(`${toDateFilter}T23:59:59`);
          if (Number.isNaN(toDt.getTime()) || dt > toDt) return false;
        }
        return true;
      });
    }
    return out;
  }, [
    data,
    searchQuery,
    cityFilter,
    dateFilter,
    fromDateFilter,
    toDateFilter,
    showSearch,
    searchKeys,
    showCityFilter,
    cityKey,
    showDateFilter,
    showDateRangeFilter,
    dateKey,
  ]);

  useEffect(() => {
    if (pageSize) setRowsPerPage(pageSize);
  }, [pageSize]);

  const effectivePageSize = pageSize ? rowsPerPage : null;
  const totalPages = Math.max(
    1,
    effectivePageSize ? Math.ceil(filteredData.length / effectivePageSize) : 1,
  );
  const paginatedData = useMemo(() => {
    if (!effectivePageSize) return filteredData;
    const start = (currentPage - 1) * effectivePageSize;
    return filteredData.slice(start, start + effectivePageSize);
  }, [filteredData, currentPage, effectivePageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const showFilters =
    showSearch ||
    (showCityFilter && cities.length > 1) ||
    showDateFilter ||
    showDateRangeFilter;
  const showCityDropdown = showCityFilter && cities.length > 1;
  const showDateDropdown = showDateFilter;
  const showDateRangeInputs = showDateRangeFilter;
  const hasActiveFilters =
    Boolean(searchQuery?.trim()) ||
    Boolean(cityFilter) ||
    Boolean(dateFilter) ||
    Boolean(fromDateFilter) ||
    Boolean(toDateFilter);
  const tableData = effectivePageSize ? paginatedData : filteredData;
  const rangeStart =
    filteredData.length === 0
      ? 0
      : (currentPage - 1) * (effectivePageSize || 0) + 1;
  const rangeEnd = effectivePageSize
    ? Math.min(currentPage * effectivePageSize, filteredData.length)
    : filteredData.length;

  const getExportValue = (col, row) => {
    if (col.exportValue && typeof col.exportValue === "function") {
      return col.exportValue(row);
    }
    const raw = row[col.dataIndex ?? col.key];
    if (raw == null) return "";
    if (typeof raw === "object") return JSON.stringify(raw);
    return String(raw).trim();
  };

  const escapeCsvCell = (str) => {
    const s = String(str ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r")) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const handleExportExcel = () => {
    const headers = columns.map((col) => escapeCsvCell(col.title));
    const rows = filteredData.map((row) =>
      columns.map((col) => escapeCsvCell(getExportValue(col, row))),
    );
    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportFileName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {(showFilters || showExport) && (
        <div className="admin-filters admin-filters-inline">
          {showSearch && (
            <div className="admin-filter-group admin-search-group">
              <label htmlFor="common-table-search" className="admin-filter-label">
                Search
              </label>
              <div className="admin-search-wrap">
                <input
                  id="common-table-search"
                  type="text"
                  className="admin-filter-input admin-search-input"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (effectivePageSize) setCurrentPage(1);
                  }}
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="admin-search-clear"
                    onClick={() => {
                      setSearchQuery("");
                      if (effectivePageSize) setCurrentPage(1);
                    }}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
          )}
          {showCityDropdown && (
            <div className="admin-filter-group admin-filter-city">
              <label htmlFor="common-table-city" className="admin-filter-label">
                City
              </label>
              <select
                id="common-table-city"
                className="admin-filter-input"
                value={cityFilter || "all"}
                onChange={(e) => {
                  setCityFilter(e.target.value === "all" ? "" : e.target.value);
                  if (effectivePageSize) setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                {cities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}
          {showDateDropdown && (
            <div className="admin-filter-group admin-filter-city">
              <label htmlFor="common-table-date" className="admin-filter-label">
                {dateLabel}
              </label>
              <input
                id="common-table-date"
                type="date"
                className="admin-filter-input"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  if (effectivePageSize) setCurrentPage(1);
                }}
              />
            </div>
          )}
          {showDateRangeInputs && (
            <>
              <div className="admin-filter-group admin-filter-city">
                <label htmlFor="common-table-date-from" className="admin-filter-label">
                  {fromDateLabel}
                </label>
                <input
                  id="common-table-date-from"
                  type="date"
                  className="admin-filter-input"
                  value={fromDateFilter}
                  onChange={(e) => {
                    setFromDateFilter(e.target.value);
                    if (effectivePageSize) setCurrentPage(1);
                  }}
                />
              </div>
              <div className="admin-filter-group admin-filter-city">
                <label htmlFor="common-table-date-to" className="admin-filter-label">
                  {toDateLabel}
                </label>
                <input
                  id="common-table-date-to"
                  type="date"
                  className="admin-filter-input"
                  value={toDateFilter}
                  onChange={(e) => {
                    setToDateFilter(e.target.value);
                    if (effectivePageSize) setCurrentPage(1);
                  }}
                />
              </div>
            </>
          )}
          {showExport && (
            <div className="admin-filter-group admin-export-group">
              <label className="admin-filter-label" htmlFor="common-table-export">
                Export
              </label>
              <button
                id="common-table-export"
                type="button"
                className="admin-export-btn"
                onClick={handleExportExcel}
                disabled={loading || filteredData.length === 0}
                aria-label="Export to Excel (CSV)"
              >
                Export to Excel
              </button>
            </div>
          )}
          {hasActiveFilters && (
            <div className="admin-filter-group admin-export-group">
              <label className="admin-filter-label" htmlFor="common-table-clear-filters">
                Filters
              </label>
              <button
                id="common-table-clear-filters"
                type="button"
                className="admin-export-btn"
                onClick={() => {
                  setSearchQuery("");
                  setCityFilter("");
                  setDateFilter("");
                  setFromDateFilter("");
                  setToDateFilter("");
                  if (effectivePageSize) setCurrentPage(1);
                }}
                aria-label="Clear all filters"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
      <div className="admin-table-wrap admin-table-wrap-scroll">
        {loading ? (
          <AdminTableSkeleton columns={columns.length} rows={skeletonRows} />
        ) : (
          <table className="admin-table admin-table-one-row">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className="admin-table-th">
                    <span className="admin-table-cell-inner">{col.title}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (selectable ? 1 : 0)}
                    className="admin-table-empty"
                  >
                    {emptyText}
                  </td>
                </tr>
              ) : (
                tableData.map((row, index) => {
                  const serialNumber = effectivePageSize
                    ? (currentPage - 1) * effectivePageSize + index + 1
                    : index + 1;
                  return (
                  <tr key={row.id ?? index} className="admin-table-tr">
                    {columns.map((col) => (
                      <td key={col.key} className="admin-table-td">
                        <span className="admin-table-cell-inner">
                          {col.render
                            ? col.render(row, serialNumber, index)
                            : (row[col.dataIndex] ?? "—")}
                        </span>
                      </td>
                    ))}
                  </tr>
                )})
              )}
            </tbody>
          </table>
        )}
      </div>
      {!loading && effectivePageSize && filteredData.length > 0 && (
        <div className="admin-table-pagination">
          <span className="admin-table-pagination-info">
            Showing {rangeStart}–{rangeEnd} of {filteredData.length}
          </span>
          <div className="admin-table-pagination-actions">
            {showPageSizeSelector && (
              <label className="admin-table-pagination-page">
                Rows:
                <select
                  className="admin-filter-input"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value) || 10);
                    setCurrentPage(1);
                  }}
                  style={{ marginLeft: 8, minWidth: 72 }}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              className="admin-table-pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              Previous
            </button>
            <span className="admin-table-pagination-page">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="admin-table-pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CommonTable;
